import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { casinoApi, type CasinoOpResult } from '@/api/casino.api';
import { chatKeys } from '@/hooks/useChats';

export const casinoKeys = {
  all: ['casino'] as const,
  balance: (clientId: string) => [...casinoKeys.all, 'balance', clientId] as const,
};

export function useCasinoBalance(clientId: string | undefined) {
  return useQuery({
    queryKey: casinoKeys.balance(clientId || ''),
    queryFn: () => casinoApi.getBalance(clientId as string),
    enabled: !!clientId,
  });
}

function onOpResult(res: CasinoOpResult | undefined, okMsg: string, errMsg: string) {
  if (res?.success) toast.success(okMsg);
  else toast.error(res?.message || errMsg);
}

export function useCargar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, amount }: { clientId: string; amount: number }) =>
      casinoApi.deposit(clientId, amount),
    onSuccess: (res, { clientId }) => {
      qc.invalidateQueries({ queryKey: casinoKeys.balance(clientId) });
      qc.invalidateQueries({ queryKey: chatKeys.lists() });
      onOpResult(res, 'Carga realizada', 'No se pudo cargar');
    },
    onError: () => toast.error('No se pudo cargar'),
  });
}

export function useRegisterCasino() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (clientId: string) => casinoApi.register(clientId),
    onSuccess: (res, clientId) => {
      qc.invalidateQueries({ queryKey: casinoKeys.balance(clientId) });
      qc.invalidateQueries({ queryKey: chatKeys.lists() });
      if (res?.ok) toast.success(res.created ? 'Jugador creado en el casino' : 'Jugador vinculado');
      else toast.error('No se pudo crear: ' + (res?.error || ''));
    },
    onError: () => toast.error('No se pudo crear el jugador'),
  });
}

export function useRetirar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ clientId, amount }: { clientId: string; amount: number }) =>
      casinoApi.withdraw(clientId, amount),
    onSuccess: (res, { clientId }) => {
      qc.invalidateQueries({ queryKey: casinoKeys.balance(clientId) });
      qc.invalidateQueries({ queryKey: chatKeys.lists() });
      onOpResult(res, 'Retiro realizado', 'No se pudo retirar');
    },
    onError: () => toast.error('No se pudo retirar'),
  });
}
